import type {
  BackpackItem, CollectResult, CollectStatus, ExtraIngredient, IngredientCatalog, IslandEnvironment, Recipe, SubmitBackpackResult,
} from './types'

const API_ORIGIN = (process.env.VUE_APP_BAR_API_ORIGIN || 'https://forum-api.islander.top').replace(/\/$/, '')
const WS_ORIGIN = (process.env.VUE_APP_BAR_WS_ORIGIN || API_ORIGIN.replace(/^http/, 'ws')).replace(/\/$/, '')

export class BarApiError extends Error {
  code: number
  error: string
  payload: Record<string, any>

  constructor(message: string, code: number, error: string, payload: Record<string, any>) {
    super(message)
    this.name = 'BarApiError'
    this.code = code
    this.error = error
    this.payload = payload
  }
}

async function getBarData<T = any>(path: string, init: RequestInit = {}, token = ''): Promise<T> {
  const headers = new Headers(init.headers || {})
  if (token) headers.set('Authorization', token)
  const response = await fetch(`${API_ORIGIN}${path}`, { ...init, headers })
  const body = await response.json()
  if (!response.ok || body.code !== 200) {
    const missing = body.error === 'missing' && Array.isArray(body.details)
      ? body.details.map((item: any) => `${item.name}还差 ${item.shortage}`).join('、')
      : ''
    throw new BarApiError(
      missing || body.msg || (body.error === 'daily_collect_limit_reached' ? '今天的搜集次数已经用完了' : `请求失败（${body.code || response.status}）`),
      Number(body.code || response.status),
      body.error || '',
      body,
    )
  }
  return body.data as T
}

export async function fetchMenu(): Promise<Recipe[]> {
  const data = await getBarData('/api/bar/menu')
  return data.recipes
}

export function fetchDrink(id: number): Promise<any> {
  return getBarData(`/api/bar/drink/${id}`)
}

export function fetchTrace(id: number): Promise<any> {
  return getBarData(`/api/bar/trace/${id}`)
}

export function fetchCollectStatus(token: string): Promise<CollectStatus> {
  return getBarData('/api/bar/collect/status', {}, token)
}

export function collectBarItem(token: string): Promise<CollectResult> {
  return getBarData('/api/bar/collect', { method: 'POST' }, token)
}

export function fetchIngredients(): Promise<IngredientCatalog> {
  return getBarData('/api/bar/ingredients')
}

export function fetchIslandEnvironment(): Promise<IslandEnvironment> {
  return getBarData('/api/island/environment')
}

export async function fetchBackpack(token: string): Promise<BackpackItem[]> {
  const data = await getBarData<{ items: BackpackItem[] }>('/api/bar/backpack', {}, token)
  return data.items || []
}

export function submitBackpackItem(token: string, typeId: number, quantity: number): Promise<SubmitBackpackResult> {
  return getBarData('/api/bar/backpack/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type_id: typeId, quantity }),
  }, token)
}

export function createOrderSocket(
  token: string,
  recipeId: number,
  extras: ExtraIngredient[],
  message: string,
  onEvent: (event: any) => void,
  onClosed: () => void,
): WebSocket {
  const socket = new WebSocket(`${WS_ORIGIN}/ws/bar/order`)
  socket.onopen = () => socket.send(JSON.stringify({
    type: 'order.create',
    token,
    payload: { recipe_id: recipeId, overrides: {}, extras, message },
  }))
  socket.onmessage = (message) => onEvent(JSON.parse(message.data))
  socket.onerror = () => onEvent({ type: 'order.failed', payload: { reason: '海上的信号断了，请稍后再试' } })
  socket.onclose = onClosed
  return socket
}
